import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import BackNavigationButton from 'components/BackNavigationButton/BackNavigationButton'
import { WppButton, WppIconEdit, WppTypography } from '@wppopen/components-library-react'
import Banner from 'components/Banner'
import useApi from 'hooks/useApi'
import usePermissions from 'hooks/usePermissions'
import useToast from 'hooks/useToast'
import { createCsApiUrlFromEndpoint, getFormattedDate } from 'utils'
import appContext from 'context/appContext'
import NotFound404Page from 'pages/NotFound404Page/NotFound404Page'
import SettingsPageContext from 'context/settingsPageContext/settingsPageContext'
import FormSkeleton from 'components/Skeletons/FormSkeleton'
import useInitialiser from 'hooks/useInitialiser'
import { unknown } from 'utils/constants'
import { CreateAndEditRegionForm } from 'components/Forms'
import type { RegionsCreatePayloadV5 } from 'utils/utils.d'
import type { RegionsDetailData } from '@contracts'
import navigationHeaderStyles from '../NavigationHeader.module.scss'
import styles from './RegionsTabView.module.scss'

export default function RegionView() {
  const { configApi } = useContext(appContext)
  const { data: configData } = configApi
  const { activeBannerId, toastDetailsContext, resetBanner } = useContext(SettingsPageContext)
  const { toastDetails, setToastDetails } = useToast()
  const { region_id } = useParams()
  const getRegionsApiMethods = useApi<RegionsDetailData>(createCsApiUrlFromEndpoint(`regions/${region_id}`))
  const {
    fetchData: getRegions,
    data: getRegionsData,
    isLoading: isGetRegionsLoading,
    error: getRegionsError,
    url: getRegionsUrl,
  } = getRegionsApiMethods

  const putRegionsApiMethods = useApi<RegionsDetailData>(createCsApiUrlFromEndpoint(`regions/${region_id}`))
  const {
    fetchData: putRegion,
    isLoading: isPutRegionLoading,
    error: putRegionError,
    url: putRegionUrl,
  } = putRegionsApiMethods
  const [isFormActive, setIsFormActive] = useState(false)

  const { clientsWithPermissions } = usePermissions({
    permissionSubset: ['PDP_APP_GET_REGIONS_BY', 'PDP_APP_PUT_REGIONS_BY'],
  })

  const [clientsWithGetSingleRegion, clientsWithPutRegion] = clientsWithPermissions

  useEffect(() => {
    getRegions(getRegionsUrl).catch(console.error)
  }, [])

  const onSubmitForm = function (data: RegionsCreatePayloadV5): Promise<string> {
    setToastDetails(undefined)
    const updateRegionData = async () => {
      try {
        // PUT new Region data
        await putRegion(putRegionUrl, {
          method: 'PUT',
          body: JSON.stringify(data),
        })
        // GET updated data from the API
        await getRegions(getRegionsUrl)

        resetBanner()
        setToastDetails({
          type: 'success',
          message: 'Region successfully edited',
        })
        setIsFormActive(false)
        return 'Region updated successfully'
      } catch (errors) {
        setToastDetails({
          type: 'error',
          message: 'Error updating region',
        })

        throw errors
      } finally {
        window.scrollTo(0, 0)
      }
    }

    // call the actual update Region data function
    return new Promise((resolve, reject) => {
      updateRegionData()
        .then(result => {
          resolve(result)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  // handle load and errors
  const { isInitialising } = useInitialiser({ subscribedApis: [getRegionsApiMethods] })

  if (!isInitialising && (!clientsWithGetSingleRegion?.length || (getRegionsError && !isGetRegionsLoading))) {
    return <NotFound404Page />
  }
  return (
    <>
      <div className={navigationHeaderStyles.navigationHeader__container}>
        <BackNavigationButton href="/settings/regions" text="Back to settings" isBottomMargin={false} />
        {/* context and local banner */}
        <Banner
          text={toastDetailsContext?.message || toastDetails?.message}
          type={toastDetailsContext?.type || toastDetails?.type}
          onBannerClick={toastDetailsContext?.onBannerClick || toastDetails?.onBannerClick}
          shouldBannerShow={(activeBannerId === region_id && !!toastDetailsContext) || !!toastDetails}
          onClose={() => {
            if (activeBannerId === region_id && !!toastDetailsContext) {
              resetBanner()
            }
            if (!!toastDetails) {
              setToastDetails(undefined)
            }
          }}
        />
      </div>
      {isInitialising ? (
        <FormSkeleton />
      ) : (
        <div className={styles.wrapper}>
          <WppTypography type="xs-midi" tag="p" className={styles.text_right}>
            {getRegionsData?.updated_at === getRegionsData?.created_at ? 'Created' : 'Updated'}:{' '}
            {getFormattedDate(getRegionsData?.updated_at)} by {getRegionsData?.update_info?.user_name || unknown}
          </WppTypography>
          <div className={styles.wrapper__header}>
            <WppTypography type="3xl-heading" tag="h1">
              {getRegionsData?.name}
            </WppTypography>
            {!!clientsWithPutRegion?.length && !isFormActive && (
              <WppButton
                onClick={() => {
                  setIsFormActive(true)
                  setToastDetails(undefined)
                }}
              >
                <WppIconEdit slot="icon-start" />
                Edit
              </WppButton>
            )}
          </div>
          <CreateAndEditRegionForm
            initialData={getRegionsData}
            isFormActive={isFormActive}
            onCancelForm={() => setIsFormActive(false)}
            onSubmitForm={onSubmitForm}
            errors={putRegionError}
            configRetailers={configData?.retailers}
            isApiLoading={isPutRegionLoading}
            setErrorToastDetails={setToastDetails}
          />
        </div>
      )}
    </>
  )
}
